import { trim as _trim, snakeCase, camelCase } from 'lodash';
import * as inflection from 'inflection';

export {
  camelCase,
  snakeCase,
}


export function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export function formatQuoteTerm(termMonths: number | null): string | null {
  if (!termMonths) {
    return null;
  }

  if (termMonths % 12 === 0) {
    const years = termMonths / 12;
    return years === 1 ? '1 year' : `${years} years`;
  } else {
    return termMonths === 1 ? '1 month' : `${termMonths} months`;
  }
}

export function isGuid(id: string) {
  const guidRegExp = new RegExp('^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$');
  return guidRegExp.test(id);
}

export function trim(s: string | null | undefined): string {
  return _trim(s ?? undefined);
}

export function downcase(s: string | null | undefined): string | null | undefined {
  if (!s) {
    return s;
  }

  return s.toLowerCase();
}

export function containsString(source: string, search: string) {
  return source.toLowerCase().indexOf(search.toLowerCase()) > -1;
}

export function nullIfEmpty(strToNull: string | null | undefined): string | null {
  return (!strToNull ? null : strToNull);
}

export function trimAndNullIfEmpty(strToNull: string | null | undefined): string | null {
  return nullIfEmpty(trim(strToNull));
}

// Inflection's underscore does not convert hyphens to underscores, so use lodash snakeCase for that
export function titleCase(s: string): string {
  return inflection.titleize(snakeCase(s));
}

export function classCase(s: string): string {
  return inflection.classify(snakeCase(s));
}

const DIGIT_WORDS = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];

/**
 * If the input starts with a digit, replace just that first character with its
 * English word equivalent ("3" → "Three", keeping the rest of the string).
 * Used to coerce identifiers into a letter-first form for systems that reject
 * digit-leading identifiers (e.g. NetSapiens domain names, DNS labels), while
 * keeping the rest of the string readable — "360DegreesGroup" becomes
 * "Three60DegreesGroup", not "ThreeSixZeroDegreesGroup".
 */
export function leadingDigitToWord(s: string | null | undefined): string | null | undefined {
  if (!s) return s;
  const first = s.charAt(0);
  if (first < '0' || first > '9') return s;
  return DIGIT_WORDS[Number(first)] + s.slice(1);
}

export function slugify(s: string | null) {
  if (!s) {
    return s;
  }

  return s.toLowerCase().replace(/[^a-z0-9]/g, '-')
}

export function replaceAll(
  inputString: string | undefined,
  charToReplace: string,
  replacementChar: string): string {
  if (!inputString) return '';
  return inputString.split(charToReplace).join(replacementChar);
}

export function isUuid(id: string) {
  if (!id) { return false; }
  return id.match(/^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/);
}

export function removeSpaces(s: string | null | undefined): string {
  if (!s) return '';
  return s.replace(/\s/g, '');
}

export function convertToAmPmFormat(value: string): string {
  // Assuming value is in the format 'HH:mm'
  const [hours, minutes] = value.split(':').map(Number);

  let amPm = 'AM';
  let formattedHours = hours;

  if (hours >= 12) {
    amPm = 'PM';
    formattedHours = hours === 12 ? 12 : hours - 12;
  }

  if (formattedHours === 0) {
    formattedHours = 12;
  }

  return `${formattedHours}:${minutes.toString().padStart(2, '0')} ${amPm}`;
}

export function maybePluralize(str: string, num: number): string {
  return num === 1 ? str : inflection.pluralize(str);
}

export function replaceDiacriticCharacters(str: string) {
  return str ? str.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : str;
}

export function removeNonAlphanumericAndBlankCharacters(str: string) {
  return str ? str.trim().replace(/[^a-zA-Z0-9 ]/g, '') : str;
}

export function splitCsvLine(line: string) {
  const fields: string[] = [];
  let inQuotes = false, s = '';
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c == ',' && !inQuotes) {
      fields.push(s);
      s = '';
    }
    else if (c == '"') {
      if (inQuotes) {
        if (line[i + 1] === '"') {
          s += c;
          i++;
        }
        else {
          inQuotes = false;
        }
      }
      else {
        inQuotes = true;
      }
    }
    else {
      s += c;
    }
  }

  fields.push(s);
  return fields;
}

export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, function (m) {
    switch (m) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return m;
    }
  });
}